import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from './Modal';
import Button from './Button';

/** GitHub-style destructive confirmation: the confirm button stays disabled
 * until the exact resource name is typed, so a stray click can't delete it. */
export default function TypeToConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message,
  confirmWord,
  confirmLabel = 'Delete',
  loading = false,
}) {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (!isOpen) setValue('');
  }, [isOpen]);

  const matches = confirmWord && value.trim() === confirmWord;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <div className="flex flex-col items-center gap-4 pt-2 pb-1 text-center">
        <div className="rounded-full bg-red-100 dark:bg-red-900/30 p-4">
          <AlertTriangle size={28} className="text-red-600 dark:text-red-400" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          {message && (
            <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{message}</p>
          )}
        </div>

        <div className="w-full text-left">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
            Type <span className="font-mono font-semibold text-gray-900 dark:text-gray-100">{confirmWord}</span> to confirm
          </label>
          <input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && matches && !loading) onConfirm(); }}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
            placeholder={confirmWord}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <div className="flex gap-3 w-full">
          <Button variant="secondary" className="flex-1" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            className="flex-1"
            onClick={onConfirm}
            loading={loading}
            disabled={!matches}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
